import { MediaStatusType } from '@lib2/model/media/MediaTypes';
import { ACTIVE_JOB_STATUSES } from '@/lib/constants/ingest';
import type { JobData, JobViewsData } from '@/lib/types/Job';

/** Media statuses that can carry an active ingest/transcode job. */
export const PROCESSING_MEDIA_STATUSES: readonly MediaStatusType[] = [
  MediaStatusType.AwaitingIngest,
  MediaStatusType.Ingesting,
  MediaStatusType.Processing,
];

/**
 * Per-media active job percents, kept per phase. Ingest and transcode rates
 * must never mix: the ingest job mirrors the client upload while the media
 * is `ingesting`, the transcode job tracks server work while `processing`.
 * A media can briefly have active jobs in both pools around the handoff.
 */
export interface MediaJobProgress {
  ingest?: number;
  transcode?: number;
}

export class JobProgressUtils {
  /**
   * Per-media percent complete (0-100) from the polled job list, one slot per
   * phase pool. Within a pool, when a media has several active jobs the most
   * recently updated one wins (legacy Job.store semantics — which also kept
   * the pools separate; merging them let an ingest rate show under a
   * Processing badge and vice versa).
   */
  static buildMediaProgressMap(jobs: JobViewsData | undefined): Map<number, MediaJobProgress> {
    const result = new Map<number, MediaJobProgress>();
    if (!jobs) return result;

    const collect = (pool: JobData[], slot: keyof MediaJobProgress) => {
      const latest = new Map<number, JobData>();
      for (const job of pool) {
        const mediaId = job.ids.mediaId;
        if (mediaId === null) continue;
        if (!(ACTIVE_JOB_STATUSES as readonly string[]).includes(job.jobStatus)) continue;
        const prev = latest.get(mediaId);
        if (!prev || job.updated > prev.updated) latest.set(mediaId, job);
      }
      for (const [mediaId, job] of latest) {
        const entry = result.get(mediaId) ?? {};
        entry[slot] = Math.min(100, Math.max(0, job.completedRate));
        result.set(mediaId, entry);
      }
    };

    collect(jobs.ingestVideoJobs, 'ingest');
    collect(jobs.transJobs, 'transcode');
    return result;
  }

  /**
   * Per-media signature of which phase pools hold an active job ('i', 't',
   * or 'it'), for refetch-trigger diffing. The videos list must refetch not
   * only when a media's jobs finish (id leaves the map) but on any phase
   * transition: at the ingest→transcode handoff the transcode job is
   * enqueued immediately, so the mediaId never leaves the active map and an
   * id-only diff misses the edge — the grid kept the stale `ingesting`
   * status ("Uploading") through the whole transcode.
   */
  static phaseSignatures(progress: Map<number, MediaJobProgress>): Map<number, string> {
    const result = new Map<number, string>();
    for (const [mediaId, entry] of progress) {
      result.set(
        mediaId,
        `${entry.ingest !== undefined ? 'i' : ''}${entry.transcode !== undefined ? 't' : ''}`
      );
    }
    return result;
  }

  /**
   * True when any media tracked in `prev` finished (left the map) or its
   * active phase set changed — the moments the backend flips
   * mediaStatusType, so the media list is worth refetching. Newly appearing
   * media don't trigger (their list row arrives via the upload-success
   * invalidation).
   */
  static hasPhaseTransition(prev: Map<number, string>, current: Map<number, string>): boolean {
    for (const [mediaId, phases] of prev) {
      if (current.get(mediaId) !== phases) return true;
    }
    return false;
  }

  /**
   * The percent a status badge may show for a media: the ingest rate while
   * the media is uploading (`awaiting_ingest`/`ingesting`), the transcode
   * rate while `processing`, nothing otherwise. Keeps the number on the
   * badge in the same phase as the label next to it.
   */
  static percentForStatus(
    entry: MediaJobProgress | undefined,
    status: MediaStatusType
  ): number | undefined {
    if (!entry) return undefined;
    if (status === MediaStatusType.AwaitingIngest || status === MediaStatusType.Ingesting) {
      return entry.ingest;
    }
    if (status === MediaStatusType.Processing) {
      return entry.transcode;
    }
    return undefined;
  }
}
